import { type HotkeyNode } from '@/hooks/use-catalog-hotkeys';
import { useHotkeyIcons, hotkeyIconSlugOf } from '@/hooks/use-hotkey-icons';
import { cn } from '@/lib/utils';
import { useT } from '@/i18n/use-t';

/**
 * Cashier-facing approximation of the hotkey panel, split by hotkey kind
 * (VL-258).
 *
 * ------------------------------------------------------------------
 * WHY THE SPLIT, AND WHY THESE THREE NAMES
 * ------------------------------------------------------------------
 * The preview used to be one undifferentiated 3-column grid with the kind
 * shown only as a background tint. The till does not present hotkeys that way:
 * it puts them in three distinct regions, and an author arranging tiles in a
 * flat grid was rehearsing a layout no cashier ever sees.
 *
 * The regions and their vocabulary are the terminal's own — `kind` here is the
 * same value the terminal parses as `CatalogButtonKind`, confirmed on VL-258:
 * "the Hotkeys Preview must fully match the terminal behavior and use the same
 * CatalogButtonKind values: group, item, and function".
 *
 *   `group`    → Catalog        `function` → Quick Actions      `item` → Hot
 *
 * The order below is the till's, top to bottom: Quick Actions, then Catalog
 * directly beneath it in the same column, then Hot in the scan row.
 *
 * This remains an approximation for authors, not the terminal UI — the point
 * is that a hotkey now previews in the region it will actually appear in.
 */

/**
 * The three regions, in the order the till stacks them.
 *
 * Each carries its catalogue key beside the English, so the headings translate
 * with the rest of the page while the table stays one readable list.
 */
const SECTIONS = [
  {
    kind: 'function',
    titleKey: 'hotkeys.preview.quickActions',
    title: 'Quick Actions',
    emptyKey: 'hotkeys.preview.emptyFunction',
    empty: 'No function hotkeys in this panel',
  },
  {
    kind: 'group',
    titleKey: 'hotkeys.preview.catalog',
    title: 'Catalog',
    emptyKey: 'hotkeys.preview.emptyGroup',
    empty: 'No group hotkeys in this panel',
  },
  {
    kind: 'item',
    titleKey: 'hotkeys.preview.hot',
    title: 'Hot',
    emptyKey: 'hotkeys.preview.emptyItem',
    empty: 'No item hotkeys in this panel',
  },
] as const;

export function PreviewPanel({
  parentId,
  parentLabel,
  // H-130: renamed from `children` to avoid shadowing React's slot.
  items,
}: {
  parentId: string | null;
  parentLabel: string;
  items: HotkeyNode[];
}) {
  const t = useT();
  // VL-260 — a custom icon previews as the glyph, not as its name. The
  // shipped icon set lives in the terminal binary and BOS has no artwork for
  // it, so those still show the name.
  const customSvg = new Map(
    (useHotkeyIcons().data ?? []).map((icon) => [icon.slug, icon.svg] as const),
  );
  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto rounded-lg border bg-muted/20 p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium">{t('hotkeys.preview.title', 'Preview')}</h3>
        <span className="text-xs text-muted-foreground">
          {parentId === null
            ? t('hotkeys.preview.rootPanel', 'Root panel')
            : t('hotkeys.preview.childrenOf', 'Children of {label}').replace(
                '{label}',
                parentLabel,
              )}
        </span>
      </div>

      {SECTIONS.map((section) => {
        // Authoring order is preserved inside a section: `items` arrives
        // sorted by sortOrder, and filtering does not disturb it. A section is
        // always rendered, even when empty — a missing heading would read as
        // "this kind is not supported here" rather than "nothing here yet".
        const sectionItems = items.filter((node) => node.kind === section.kind);
        return (
          <section key={section.kind} className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(section.titleKey, section.title)}
              </h4>
              <span className="text-[10px] text-muted-foreground">{sectionItems.length}</span>
            </div>
            {sectionItems.length === 0 ? (
              <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs italic text-muted-foreground">
                {t(section.emptyKey, section.empty)}
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {sectionItems.map((node) => (
                  <PreviewTile key={node.id} node={node} customSvg={customSvg} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function PreviewTile({ node, customSvg }: { node: HotkeyNode; customSvg: Map<string, string> }) {
  const slug = node.iconName === null ? null : hotkeyIconSlugOf(node.iconName);
  const svg = slug === null ? undefined : customSvg.get(slug);
  // The tint is now secondary to the section heading, but kept: it is what
  // distinguishes a tile once it has been dragged into the tree beside others.
  const accent =
    node.kind === 'group'
      ? 'bg-secondary text-secondary-foreground'
      : node.kind === 'function'
        ? 'bg-primary/10 text-primary border-primary/30'
        : 'bg-card';
  return (
    <div
      className={cn(
        'flex aspect-square flex-col items-center justify-center rounded-md border p-2 text-center text-xs',
        accent,
      )}
    >
      {svg !== undefined ? (
        <span
          // 17 px inside a 30x30 container — the Quick Actions geometry from
          // the terminal prototype, so the author sees the glyph at the size a
          // cashier will rather than blown up to fill the tile.
          className="mb-1 grid h-[30px] w-[30px] place-items-center rounded-[9px] bg-background/60"
          aria-hidden
        >
          <span
            className="block h-[17px] w-[17px] [&>svg]:h-full [&>svg]:w-full"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </span>
      ) : (
        node.iconName !== null && (
          <span className="mb-1 text-[10px] text-muted-foreground">{node.iconName}</span>
        )
      )}
      <span className="line-clamp-2 font-medium">{node.label}</span>
      {node.kind === 'function' && node.payload !== null && (
        <span className="mt-1 text-[9px] uppercase text-muted-foreground">{node.payload}</span>
      )}
    </div>
  );
}
